import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { TaskProvider } from "./contexts/TaskContext";
import { BillingProvider } from "./contexts/BillingContext";
import { Toaster } from "sonner";

import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import CalendarPage from "./pages/CalendarPage";
import NotificationsPage from "./pages/NotificationsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import SettingsPage from "./pages/SettingsPage";
import FamilyPage from "./pages/FamilyPage";
import TemplatesPage from "./pages/TemplatesPage";
import RapidCapturePage from "./pages/RapidCapturePage";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./pages/AuthCallback";
import Pricing from "./pages/Pricing";
import AppLayout from "./components/AppLayout";
import InstallPrompt from "./components/InstallPrompt";
import UpgradeRequiredDialog from "./components/UpgradeRequiredDialog";
import AdminInviteCodes from "./pages/AdminInviteCodes";
import RedeemPage from "./pages/RedeemPage";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-sm text-muted-foreground font-mono">Loading FlowTasks360…</div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (window.location.hash?.includes("session_id=")) return <AuthCallback />;
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (user.role !== "admin") return <Navigate to="/personal" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (window.location.hash?.includes("session_id=")) return <AuthCallback />;
  if (user === null) return <Loading />;
  if (user) return <Navigate to="/personal" replace />;
  return children;
}

function Shell() {
  const { user } = useAuth();
  const isAuthed = !!user;
  return (
    <ThemeProvider isAuthed={isAuthed}>
      <BillingProvider isAuthed={isAuthed}>
        <TaskProvider isAuthed={isAuthed}>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route
              path="/capture"
              element={
                <ProtectedRoute>
                  <RapidCapturePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/redeem/:code"
              element={
                <ProtectedRoute>
                  <RedeemPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/personal" replace />} />
              <Route path="personal" element={<Dashboard type="personal" />} />
              <Route path="family" element={<Dashboard type="family" />} />
              <Route path="work" element={<Dashboard type="work" />} />
              <Route path="school" element={<Dashboard type="school" />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="templates" element={<TemplatesPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="family-workspace" element={<FamilyPage />} />
              <Route path="pricing" element={<Pricing />} />
              <Route
                path="admin/invite-codes"
                element={
                  <AdminRoute>
                    <AdminInviteCodes />
                  </AdminRoute>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/personal" replace />} />
          </Routes>
          {isAuthed && <InstallPrompt />}
          {isAuthed && <UpgradeRequiredDialog />}
        </TaskProvider>
      </BillingProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Shell />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
